/**
 * Shared USPS state / territory codes and names for location parsing and admin UI.
 * Single source of truth — do not duplicate abbreviation lists elsewhere.
 */

export const US_STATE_CODES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "AS",
  "GU",
  "MP",
  "PR",
  "VI",
] as const;

export type UsStateCode = (typeof US_STATE_CODES)[number];

export const US_STATE_CODE_SET: ReadonlySet<string> = new Set(US_STATE_CODES);

/** Full / alternate names → USPS abbreviation (uppercase keys). */
export const US_STATE_NAME_TO_CODE: Readonly<Record<string, UsStateCode>> = {
  ALABAMA: "AL",
  ALASKA: "AK",
  ARIZONA: "AZ",
  ARKANSAS: "AR",
  CALIFORNIA: "CA",
  COLORADO: "CO",
  CONNECTICUT: "CT",
  DELAWARE: "DE",
  FLORIDA: "FL",
  GEORGIA: "GA",
  HAWAII: "HI",
  IDAHO: "ID",
  ILLINOIS: "IL",
  INDIANA: "IN",
  IOWA: "IA",
  KANSAS: "KS",
  KENTUCKY: "KY",
  LOUISIANA: "LA",
  MAINE: "ME",
  MARYLAND: "MD",
  MASSACHUSETTS: "MA",
  MICHIGAN: "MI",
  MINNESOTA: "MN",
  MISSISSIPPI: "MS",
  MISSOURI: "MO",
  MONTANA: "MT",
  NEBRASKA: "NE",
  NEVADA: "NV",
  "NEW HAMPSHIRE": "NH",
  "NEW JERSEY": "NJ",
  "NEW MEXICO": "NM",
  "NEW YORK": "NY",
  "NORTH CAROLINA": "NC",
  "NORTH DAKOTA": "ND",
  OHIO: "OH",
  OKLAHOMA: "OK",
  OREGON: "OR",
  PENNSYLVANIA: "PA",
  "RHODE ISLAND": "RI",
  "SOUTH CAROLINA": "SC",
  "SOUTH DAKOTA": "SD",
  TENNESSEE: "TN",
  TEXAS: "TX",
  UTAH: "UT",
  VERMONT: "VT",
  VIRGINIA: "VA",
  WASHINGTON: "WA",
  "WEST VIRGINIA": "WV",
  WISCONSIN: "WI",
  WYOMING: "WY",
  "DISTRICT OF COLUMBIA": "DC",
  "WASHINGTON DC": "DC",
  "WASHINGTON D.C.": "DC",
  "PUERTO RICO": "PR",
  GUAM: "GU",
  "AMERICAN SAMOA": "AS",
  "NORTHERN MARIANA ISLANDS": "MP",
  "U.S. VIRGIN ISLANDS": "VI",
  "US VIRGIN ISLANDS": "VI",
  "VIRGIN ISLANDS": "VI",
};

export const US_STATE_OPTIONS = US_STATE_CODES.map((code) => {
  const canonicalName =
    Object.entries(US_STATE_NAME_TO_CODE).find(
      ([, mappedCode]) => mappedCode === code
    )?.[0] ?? code;
  const name = canonicalName
    .toLowerCase()
    .replace(/(^|\s)[a-z]/g, (character) => character.toUpperCase())
    .replace(/^U\.s\./, "U.S.")
    .replace(/\bOf\b/, "of");

  return { code, name };
});

/** Longest names first so "New York" wins over "York". */
const STATE_NAMES_LONGEST_FIRST = Object.keys(US_STATE_NAME_TO_CODE).sort(
  (a, b) => b.length - a.length
);

/**
 * Two-letter codes that are also common English words.
 * Bare trailing matches (e.g. "Contact ME") are rejected unless the
 * string looks like a location (comma, prior place token, or ZIP).
 */
const AMBIGUOUS_TRAILING_STATE_CODES: ReadonlySet<string> = new Set([
  "IN",
  "OR",
  "ME",
  "OH",
  "OK",
  "HI",
  "ID",
]);

/** Preceding tokens that mean the trailing code is prose, not a state. */
const NON_LOCATION_PREDECESSORS: ReadonlySet<string> = new Set([
  "CONTACT",
  "EITHER",
  "GIVE",
  "TELL",
  "CALL",
  "EMAIL",
  "TEXT",
  "SEND",
  "GO",
  "SEE",
  "ASK",
  "FIND",
  "SHOW",
  "PLEASE",
  "THANKS",
  "THANK",
  "MESSAGE",
  "REPLY",
  "REACH",
  "PING",
  "LET",
  "TO",
  "FOR",
  "FROM",
  "WITH",
  "AND",
  "AT",
]);

export function isUsStateCode(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  return US_STATE_CODE_SET.has(value.trim().toUpperCase());
}

/**
 * Resolve a single token or short phrase to a USPS state code.
 * Accepts abbreviations ("MN") or full names ("Minnesota", "New York").
 */
export function resolveUsStateCode(
  raw: string | null | undefined
): UsStateCode | null {
  if (!raw?.trim()) {
    return null;
  }
  const upper = raw.trim().toUpperCase().replace(/\./g, "");
  if (US_STATE_CODE_SET.has(upper)) {
    return upper as UsStateCode;
  }
  const normalized = upper.replace(/\s+/g, " ");
  const fromName = US_STATE_NAME_TO_CODE[normalized];
  return fromName ?? null;
}

/** Alternation for regex: AL|AK|… (abbreviations only). */
export const US_STATE_CODE_ALTERNATION = US_STATE_CODES.join("|");

function acceptAmbiguousTrailingAbbrev(
  code: string,
  text: string,
  hasZip: boolean
): boolean {
  if (hasZip || !AMBIGUOUS_TRAILING_STATE_CODES.has(code)) {
    return true;
  }
  const trimmed = text.trim();
  // Whole string is just the code ("OR", "ME").
  if (trimmed.toUpperCase() === code) {
    return true;
  }
  // ", OR" / ", ME" — standard city, ST form.
  if (new RegExp(`,\\s*${code}\\s*$`, "i").test(trimmed)) {
    return true;
  }
  // Preceded by a place-like token of 3+ letters ("Portland OR", "Bangor ME"),
  // but not by common prose words ("Contact ME", "Either OR").
  const prior = trimmed.match(
    new RegExp(`\\b([A-Za-z]{3,})\\s+${code}\\s*$`, "i")
  );
  if (prior?.[1] && !NON_LOCATION_PREDECESSORS.has(prior[1].toUpperCase())) {
    return true;
  }
  return false;
}

/**
 * Try to pull a state code from the end of a location string
 * (abbreviation or full name, optionally before a ZIP).
 */
export function extractUsStateCodeFromLocationTail(
  location: string
): UsStateCode | null {
  const text = location.trim();
  if (!text) {
    return null;
  }

  // Full name before optional ZIP: "… Minnesota 55401" / "… New York"
  for (const name of STATE_NAMES_LONGEST_FIRST) {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(
      `(?:^|[,\\s])(${escaped})(?:\\s+\\d{5}(?:-\\d{4})?)?\\s*$`,
      "i"
    );
    if (re.test(text)) {
      return US_STATE_NAME_TO_CODE[name] ?? null;
    }
  }

  // Abbreviation before optional ZIP: "… MN" / "… MN 55401"
  const abbrev = text.match(
    new RegExp(
      `\\b(${US_STATE_CODE_ALTERNATION})(?:\\s+(\\d{5})(?:-\\d{4})?)?\\s*$`,
      "i"
    )
  );
  if (abbrev?.[1]) {
    const code = abbrev[1].toUpperCase() as UsStateCode;
    const hasZip = Boolean(abbrev[2]);
    if (acceptAmbiguousTrailingAbbrev(code, text, hasZip)) {
      return code;
    }
  }

  return null;
}
